import { createForm, useStore } from "@tanstack/solid-form";
import { createSignal, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { LocationPopoverForm } from "@/components/shared/geo-code/location-popover";
import { RateInput } from "@/components/shared/input";
import { Button } from "@/components/ui/button";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldGroup,
  FieldInfo,
  FieldSectionTitle,
  Input,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch, SwitchControl, SwitchThumb } from "@/components/ui/switch";
import { parseInputValue } from "@/libs/format";
import {
  createCurrencyValidatorWithMinSchema,
  MIN_AMOUNT_EUR,
} from "@/libs/validators/currency";
import {
  DURATION,
  type Duration,
  EFFORT,
  type Effort,
  EXPERIENCE_LEVEL,
  type ExperienceLevel,
  PAYMENT_TYPE,
  type PaymentType,
  REMOTE_POLICY,
  RemotePolicy,
  START_DATE,
  type StartDate,
} from "../../../../create/constants";
import { useRefineStore } from "../../store/RefineProvider";
import { detailsEditSchema } from "../../validators";
import { RefineEditDialog } from "../RefineEditDialog";
import { RefineStepCard } from "../RefineStepCard";

const { validationFunction: salaryRangeMinValidator } =
  createCurrencyValidatorWithMinSchema(MIN_AMOUNT_EUR);
const { validationFunction: salaryRangeMaxValidator } =
  createCurrencyValidatorWithMinSchema(MIN_AMOUNT_EUR);

export function DetailsStep() {
  const { t } = useI18n();
  const { state, updateFields } = useRefineStore();
  const [editOpen, setEditOpen] = createSignal(false);

  const form = createForm(() => ({
    defaultValues: {
      payment_type: state.payment_type,
      remote_policy: state.remote_policy,
      duration: state.duration,
      experience_level: state.experience_level,
      effort: state.effort,
      start_date: state.start_date,
      budget: state.budget,
      daily_rate_min:
        state.daily_rate_min != null ? String(state.daily_rate_min) : "",
      daily_rate_max:
        state.daily_rate_max != null ? String(state.daily_rate_max) : "",
      location: state.location ?? "",
      renewable: state.renewable,
    },
    onSubmit: ({ value }) => {
      const toNum = (v: string) => {
        const n = Number.parseFloat(parseInputValue(v));
        return Number.isNaN(n) || n === 0 ? null : n;
      };
      const result = detailsEditSchema.safeParse({
        ...value,
        daily_rate_min: toNum(value.daily_rate_min),
        daily_rate_max: toNum(value.daily_rate_max),
      });
      if (!result.success) return;
      const data = result.data;
      const updates = {
        ...data,
        location:
          data.remote_policy === RemotePolicy.REMOTE
            ? null
            : data.location || null,
        daily_rate_min:
          data.payment_type === "fixed" ? null : data.daily_rate_min,
        daily_rate_max:
          data.payment_type === "fixed" ? null : data.daily_rate_max,
        budget: data.payment_type === "fixed" ? data.budget : null,
      };
      updateFields(updates);
      setEditOpen(false);
    },
  }));

  const currentPaymentType = useStore(form.store, (s) => s.values.payment_type);

  const handleOpenEdit = () => {
    form.reset();
    form.setFieldValue("payment_type", state.payment_type);
    form.setFieldValue("remote_policy", state.remote_policy);
    form.setFieldValue("duration", state.duration);
    form.setFieldValue("experience_level", state.experience_level);
    form.setFieldValue("effort", state.effort);
    form.setFieldValue("start_date", state.start_date);
    form.setFieldValue("budget", state.budget);
    form.setFieldValue(
      "daily_rate_min",
      state.daily_rate_min != null ? String(state.daily_rate_min) : "",
    );
    form.setFieldValue(
      "daily_rate_max",
      state.daily_rate_max != null ? String(state.daily_rate_max) : "",
    );
    form.setFieldValue("location", state.location ?? "");
    form.setFieldValue("renewable", state.renewable);
    setEditOpen(true);
  };

  const calculatedDailyRate = () => {
    if (state.daily_rate_min && state.daily_rate_max) {
      return {
        min: state.daily_rate_min,
        max: state.daily_rate_max,
      };
    }
    return null;
  };

  return (
    <>
      <RefineStepCard
        title={t("jobOffers.refine.steps.details")}
        onEdit={handleOpenEdit}
      >
        <div class="grid grid-cols-2 gap-3 text-sm">
          <Show when={state.payment_type}>
            {(pt) => (
              <div>
                <p class="text-muted-foreground">
                  {t("jobOffers.refine.paymentTypeLabel")}
                </p>
                <p class="font-medium">
                  {t(`joblist.jobOffer.paymentType.${pt()}`)}
                </p>
              </div>
            )}
          </Show>
          <Show when={state.budget}>
            {(b) => (
              <div>
                <p class="text-muted-foreground">
                  {t("application.header.budget_label")}
                </p>
                <p class="font-medium">
                  {b()} {state.currency}
                </p>
              </div>
            )}
          </Show>
          <Show when={state.daily_rate_min || state.daily_rate_max}>
            <div>
              <p class="text-muted-foreground">
                {t("jobOffers.refine.salaryRange")}
              </p>
              <p class="font-medium">
                {calculatedDailyRate()
                  ? `${calculatedDailyRate()?.min} - ${calculatedDailyRate()?.max}`
                  : "—"}{" "}
                {state.currency}
              </p>
            </div>
          </Show>
          <Show when={state.remote_policy}>
            {(rp) => (
              <div>
                <p class="text-muted-foreground">
                  {t("jobOffers.refine.remotePolicyLabel")}
                </p>
                <p class="font-medium">
                  {t(`common.remote_policy_options.${rp()}`)}
                </p>
              </div>
            )}
          </Show>
          <Show when={state.location}>
            {(loc) => (
              <div>
                <p class="text-muted-foreground">
                  {t("joblist.jobOffer.application.location")}
                </p>
                <p class="font-medium">{loc()}</p>
              </div>
            )}
          </Show>
          <Show when={state.duration}>
            {(d) => (
              <div>
                <p class="text-muted-foreground">
                  {t("jobOffers.create.detail.form.duration")}
                </p>
                <p class="font-medium">{t(`common.duration_options.${d()}`)}</p>
              </div>
            )}
          </Show>
          <Show when={state.experience_level}>
            {(el) => (
              <div>
                <p class="text-muted-foreground">
                  {t("jobOffers.create.detail.form.experienceLevel")}
                </p>
                <p class="font-medium">
                  {t(`common.experience_level_options.${el()}`)}
                </p>
              </div>
            )}
          </Show>
          <Show when={state.effort}>
            {(e) => (
              <div>
                <p class="text-muted-foreground">
                  {t("joblist.jobOffer.application.effort")}
                </p>
                <p class="font-medium">{t(`joblist.jobOffer.effort.${e()}`)}</p>
              </div>
            )}
          </Show>
          <Show when={state.start_date}>
            {(sd) => (
              <div>
                <p class="text-muted-foreground">
                  {t("joblist.jobOffer.application.startDate")}
                </p>
                <p class="font-medium">
                  {t(`common.start_date_options.${sd()}`)}
                </p>
              </div>
            )}
          </Show>
          <Show when={state.renewable}>
            <div>
              <p class="text-muted-foreground">{t("common.renewable")}</p>
              <p class="font-medium">{t("common.yes")}</p>
            </div>
          </Show>
        </div>
      </RefineStepCard>

      <RefineEditDialog
        open={editOpen()}
        onOpenChange={setEditOpen}
        title={t("jobOffers.refine.editDetails")}
      >
        <div class="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <FieldGroup>
            <FieldSectionTitle class="mt-0">
              {t("jobOffers.refine.sectionCompensation")}
            </FieldSectionTitle>
            <form.Field
              name="payment_type"
              validators={{
                onChange: detailsEditSchema.shape.payment_type,
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t("jobOffers.refine.paymentTypeLabel")}
                  </FieldContainerLabel>
                  <Select
                    value={field().state.value}
                    onChange={(v: PaymentType | null) => {
                      field().handleChange(v);
                      if (v === "fixed") {
                        form.setFieldValue("daily_rate_min", "");
                        form.setFieldValue("daily_rate_max", "");
                      } else if (v === "daily") {
                        form.setFieldValue("budget", null);
                      }
                    }}
                    options={[...PAYMENT_TYPE]}
                    itemComponent={(props) => (
                      <SelectItem item={props.item}>
                        {
                          t(
                            `joblist.jobOffer.provider_payment_type.${props.item.rawValue}`,
                          ) as string
                        }
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<PaymentType>>
                        {(st) =>
                          t(
                            `joblist.jobOffer.paymentType.${st.selectedOption()}`,
                          ) as string
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>

            {/* Budget — only when FIXED */}
            <Show when={currentPaymentType() === "fixed"}>
              <form.Field
                name="budget"
                validators={{
                  onChange: detailsEditSchema.shape.budget,
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("application.header.budget_label")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="number"
                      value={field().state.value ?? ""}
                      onInput={(e) => {
                        const v = e.currentTarget.value;
                        field().handleChange(v ? Number(v) : null);
                      }}
                      onBlur={field().handleBlur}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
            </Show>

            {/* Daily rate range — for daily payment type */}
            <Show when={currentPaymentType() === "daily"}>
              <form.Subscribe
                selector={(s) => ({
                  daily_rate_min: s.values.daily_rate_min,
                  daily_rate_max: s.values.daily_rate_max,
                })}
              >
                {(subscribed) => {
                  const runMinValidator = (value: string) => {
                    const result = salaryRangeMinValidator(value);
                    if (!result.isValid) return result.error;
                    const max = Number.parseFloat(
                      parseInputValue(subscribed().daily_rate_max),
                    );
                    if (max === 0) return undefined;
                    const min = Number.parseFloat(parseInputValue(value));
                    if (min > max)
                      return t("jobOffers.create.detail.form.salaryMinError");
                    return undefined;
                  };
                  const runMaxValidator = (value: string) => {
                    const result = salaryRangeMaxValidator(value);
                    if (!result.isValid) return result.error;
                    const min = Number.parseFloat(
                      parseInputValue(subscribed().daily_rate_min),
                    );
                    const max = Number.parseFloat(parseInputValue(value));
                    if (min > 0 && max > 0 && max <= min)
                      return t("jobOffers.create.detail.form.salaryMaxError");
                    return undefined;
                  };
                  return (
                    <div class="grid grid-cols-2 gap-3">
                      <form.Field
                        name="daily_rate_min"
                        validators={{
                          onChange: ({ value }) => runMinValidator(value),
                          onBlur: ({ value }) => runMinValidator(value),
                        }}
                      >
                        {(field) => (
                          <FieldContainer>
                            <FieldContainerLabel for={field().name}>
                              {t("jobOffers.refine.salaryMin")}
                            </FieldContainerLabel>
                            <RateInput
                              fieldApi={field()}
                              value={field().state.value ?? ""}
                              currency={state.currency}
                              class="w-full"
                            />
                            <FieldInfo field={field()} />
                          </FieldContainer>
                        )}
                      </form.Field>
                      <form.Field
                        name="daily_rate_max"
                        validators={{
                          onChange: ({ value }) => runMaxValidator(value),
                          onBlur: ({ value }) => runMaxValidator(value),
                        }}
                      >
                        {(field) => (
                          <FieldContainer>
                            <FieldContainerLabel for={field().name}>
                              {t("jobOffers.refine.salaryMax")}
                            </FieldContainerLabel>
                            <RateInput
                              fieldApi={field()}
                              value={field().state.value ?? ""}
                              currency={state.currency}
                              class="w-full"
                            />
                            <FieldInfo field={field()} />
                          </FieldContainer>
                        )}
                      </form.Field>
                    </div>
                  );
                }}
              </form.Subscribe>
            </Show>
          </FieldGroup>

          <FieldSectionTitle class="mt-0">
            {t("jobOffers.refine.sectionWorkMode")}
          </FieldSectionTitle>
          <FieldGroup>
            <form.Field
              name="remote_policy"
              validators={{
                onChange: detailsEditSchema.shape.remote_policy,
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t("jobOffers.refine.remotePolicyLabel")}
                  </FieldContainerLabel>
                  <Select
                    value={field().state.value}
                    onChange={(v: RemotePolicy | null) => {
                      field().handleChange(v);
                      if (v === RemotePolicy.REMOTE) {
                        form.setFieldValue("location", "");
                      }
                    }}
                    options={[...REMOTE_POLICY]}
                    itemComponent={(props) => (
                      <SelectItem item={props.item}>
                        {t(
                          `common.remote_policy_options.${props.item.rawValue}`,
                        )}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<RemotePolicy>>
                        {(st) =>
                          t(
                            `common.remote_policy_options.${st.selectedOption()}`,
                          )
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>
            <form.Subscribe
              selector={(s) => ({ remote_policy: s.values.remote_policy })}
            >
              {(subscribed) => (
                <Show
                  when={
                    subscribed().remote_policy === RemotePolicy.ONSITE ||
                    subscribed().remote_policy === RemotePolicy.HYBRID
                  }
                >
                  <form.Field name="location">
                    {(field) => (
                      <FieldContainer>
                        <FieldContainerLabel for={field().name}>
                          {t("joblist.jobOffer.application.location")}
                        </FieldContainerLabel>
                        <LocationPopoverForm fieldsApi={field()} />
                        <FieldInfo field={field()} />
                      </FieldContainer>
                    )}
                  </form.Field>
                </Show>
              )}
            </form.Subscribe>
          </FieldGroup>

          <FieldSectionTitle>
            {t("jobOffers.refine.sectionContract")}
          </FieldSectionTitle>
          <FieldGroup class="grid grid-cols-2">
            <form.Field
              name="duration"
              validators={{
                onChange: detailsEditSchema.shape.duration,
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t("jobOffers.create.detail.form.duration")}
                  </FieldContainerLabel>
                  <Select
                    value={field().state.value}
                    onChange={(v: Duration | null) => field().handleChange(v)}
                    options={[...DURATION]}
                    itemComponent={(props) => (
                      <SelectItem item={props.item}>
                        {t(`common.duration_options.${props.item.rawValue}`)}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<Duration>>
                        {(st) =>
                          t(`common.duration_options.${st.selectedOption()}`)
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>

            <form.Field
              name="experience_level"
              validators={{
                onChange: detailsEditSchema.shape.experience_level,
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t("jobOffers.create.detail.form.experienceLevel")}
                  </FieldContainerLabel>
                  <Select
                    value={field().state.value}
                    onChange={(v: ExperienceLevel | null) =>
                      field().handleChange(v)
                    }
                    options={[...EXPERIENCE_LEVEL]}
                    itemComponent={(props) => (
                      <SelectItem item={props.item}>
                        {t(
                          `common.experience_level_options.${props.item.rawValue}`,
                        )}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<ExperienceLevel>>
                        {(st) =>
                          t(
                            `common.experience_level_options.${st.selectedOption()}`,
                          )
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>

            <form.Field
              name="effort"
              validators={{
                onChange: detailsEditSchema.shape.effort,
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t("joblist.jobOffer.application.effort")}
                  </FieldContainerLabel>
                  <Select
                    value={field().state.value}
                    onChange={(v: Effort | null) => field().handleChange(v)}
                    options={[...EFFORT]}
                    itemComponent={(props) => (
                      <SelectItem item={props.item}>
                        {t(`joblist.jobOffer.effort.${props.item.rawValue}`)}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<Effort>>
                        {(st) =>
                          t(`joblist.jobOffer.effort.${st.selectedOption()}`)
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>
            <form.Field
              name="start_date"
              validators={{
                onChange: detailsEditSchema.shape.start_date,
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t("joblist.jobOffer.application.startDate")}
                  </FieldContainerLabel>
                  <Select
                    value={field().state.value}
                    onChange={(v: StartDate | null) => field().handleChange(v)}
                    options={[...START_DATE]}
                    itemComponent={(props) => (
                      <SelectItem item={props.item}>
                        {t(`common.start_date_options.${props.item.rawValue}`)}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<StartDate>>
                        {(st) =>
                          t(`common.start_date_options.${st.selectedOption()}`)
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>
          </FieldGroup>

          <form.Field name="renewable">
            {(field) => (
              <div class="flex items-center gap-3">
                <Switch
                  checked={field().state.value}
                  onChange={(checked) => field().handleChange(checked)}
                >
                  <SwitchControl>
                    <SwitchThumb />
                  </SwitchControl>
                </Switch>
                <span class="text-sm">{t("common.renewable")}</span>
              </div>
            )}
          </form.Field>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setEditOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={form.handleSubmit}>{t("common.save")}</Button>
        </div>
      </RefineEditDialog>
    </>
  );
}
